export type DocumentViewerKind = 'pdf' | 'image' | 'html' | 'text' | 'markdown' | 'rtf' | 'unknown';

function getExtensionFromString(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Handle either a filename (foo.pdf) or a URL with query params.
  const noQuery = trimmed.split('?')[0] ?? trimmed;
  const lastSegment = noQuery.split('/').pop() ?? noQuery;
  const dot = lastSegment.lastIndexOf('.');
  if (dot === -1) return null;
  const ext = lastSegment
    .slice(dot + 1)
    .trim()
    .toLowerCase();
  return ext || null;
}

function normalizeFormat(format?: string | null): string | null {
  const f = typeof format === 'string' ? format.trim().toLowerCase() : '';
  return f || null;
}

export function getDocumentViewerKind(args: {
  documentFormat?: string | null;
  documentName?: string | null;
  fileUrl?: string | null;
}): DocumentViewerKind {
  const format = normalizeFormat(args.documentFormat);
  const extFromName = args.documentName ? getExtensionFromString(args.documentName) : null;
  const extFromUrl = args.fileUrl ? getExtensionFromString(args.fileUrl) : null;

  const ext = format ?? extFromName ?? extFromUrl;
  if (!ext) return 'unknown';

  // The BE serves these as a PDF rendition.
  if (['doc', 'docx', 'xls', 'xlsx', 'pdf', 'html', 'htm', 'msg', 'eml'].includes(ext)) return 'pdf';
  if (['png', 'jpg', 'jpeg'].includes(ext)) return 'image';
  if (['md', 'markdown'].includes(ext)) return 'markdown';
  if (ext === 'txt') return 'text';
  if (ext === 'rtf') return 'rtf';

  return 'unknown';
}

/** Formats the FE renders from the original file, so the BE's `isDisplayable: false` doesn't apply. */
export function isRenderedFromOriginal(args: Parameters<typeof getDocumentViewerKind>[0]): boolean {
  return ['text', 'markdown', 'rtf'].includes(getDocumentViewerKind(args));
}

export function isDisplayableDocument(document: {
  isDisplayable?: boolean | null;
  documentFormat?: string | null;
  documentName?: string | null;
}): boolean {
  return document.isDisplayable !== false || isRenderedFromOriginal(document);
}
